import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { cacheService } from "../../Core/Api/services";
import {
  maxSliderWidth,
  minSliderWidthLeft,
  minSliderWidthRight,
  pinnedSliderWidth,
} from "../../Layouts/Dashboard/dashboard";
import {
  GetLeftMounted,
  GetLeftPanelInfos,
  GetRightMounted,
  GetRightPanelInfos,
  SetLeftPanelPinned,
  SetRightPanelPinned,
} from "../../Redux/Reducers/redux-slice-layout";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

const PANEL_TRANSITION = "width 0.2s ease-in-out";

export const useResizable = () => {
  const initialWidthL = cacheService.GetLeftPanelWidth();
  const initialWidthR = cacheService.GetRightPanelWidth();

  const keepWidthL = useRef(initialWidthL);
  const widthRefL = useRef(initialWidthL ?? 200);
  const keepWidthR = useRef(initialWidthR);
  const widthRefR = useRef(initialWidthR ?? 200);

  const leftPanel = useSelector(GetLeftPanelInfos);
  const rightPanel = useSelector(GetRightPanelInfos);

  const leftMounted = useSelector(GetLeftMounted);
  const rightMounted = useSelector(GetRightMounted);

  const leftPanelRef = useRef<any>(undefined);
  const rightPanelRef = useRef<any>(undefined);
  const centerPanelRef = useRef<any>(undefined);

  const leftOpenRef = useRef(false);
  const leftPinnedRef = useRef(false);
  const rightOpenRef = useRef(false);
  const rightPinnedRef = useRef(false);

  useLayoutEffect(() => {
    leftOpenRef.current = leftPanel.open;
  }, [leftPanel.open]);
  useLayoutEffect(() => {
    rightOpenRef.current = rightPanel.open;
  }, [rightPanel.open]);
  useLayoutEffect(() => {
    leftPinnedRef.current = leftPanel.pinned;
  }, [leftPanel.pinned]);
  useLayoutEffect(() => {
    rightPinnedRef.current = rightPanel.pinned;
  }, [rightPanel.open]);

  useEffect(() => {
    leftPanelRef.current = document.querySelector(`.left-wrapper-resizable`);
    centerPanelRef.current = document.querySelector(".center-panel");
    leftPanelRef.current.style.transition = PANEL_TRANSITION;
    centerPanelRef.current.style.transition = PANEL_TRANSITION;
    const resizerElementL = leftPanelRef.current?.querySelector(".resizer--r");
    resizerElementL?.addEventListener("mousedown", handleMouseDownL);

    return () => {
      resizerElementL?.removeEventListener("mousedown", handleMouseDownL);
    };
  }, [leftMounted]);

  useEffect(() => {
    rightPanelRef.current = document.querySelector(`.right-wrapper-resizable`);
    centerPanelRef.current = document.querySelector(".center-panel");
    rightPanelRef.current.style.transition = PANEL_TRANSITION;
    centerPanelRef.current.style.transition = PANEL_TRANSITION;
    const resizerElementR = rightPanelRef.current?.querySelector(".resizer--r");
    resizerElementR?.addEventListener("mousedown", handleMouseDownR);

    return () => {
      resizerElementR?.removeEventListener("mousedown", handleMouseDownR);
    };
  }, [rightMounted]);

  const handleMouseDownL = React.useCallback((e: any) => {
    const node = leftPanelRef.current;

    if (!node) {
      return;
    }
    node.style.transition = "";
    centerPanelRef.current.style.transition = "";
    const startPos = {
      x: e.clientX,
      y: e.clientY,
    };
    const styles = window.getComputedStyle(node);
    const w = parseInt(styles.width, 10);
    const handleMouseMoveL = (e: any) => {
      const dx = e.clientX - startPos.x;
      widthRefL.current = w + dx;
      if (w + dx > minSliderWidthLeft && w + dx < maxSliderWidth) {
        cacheService.SetLeftPanelWidth(w + dx);
      }

      updateCursor();
      updateLayoutL();
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMoveL);
      document.removeEventListener("mouseup", handleMouseUp);
      node.style.transition = PANEL_TRANSITION;
      resetCursor();
    };

    document.addEventListener("mousemove", handleMouseMoveL);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  const handleMouseDownR = React.useCallback((e: any) => {
    const node = rightPanelRef.current;

    if (!node) {
      return;
    }
    node.style.transition = "";
    centerPanelRef.current.style.transition = "";
    const startPos = {
      x: e.clientX,
      y: e.clientY,
    };
    const styles = window.getComputedStyle(node);
    const w = parseInt(styles.width, 10);

    const handleMouseMoveR = (e: any) => {
      const dx = startPos.x - e.clientX;

      widthRefR.current = w + dx;
      if (w + dx > minSliderWidthRight && w + dx < maxSliderWidth) {
        cacheService.SetRightPanelWidth(w + dx);
      }

      updateCursor();
      updateLayoutR();
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMoveR);

      document.removeEventListener("mouseup", handleMouseUp);
      node.style.transition = PANEL_TRANSITION;
      resetCursor();
    };

    document.addEventListener("mousemove", handleMouseMoveR);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  const updateCursor = () => {
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const resetCursor = () => {
    centerPanelRef.current.style.transition = PANEL_TRANSITION;
    document.body.style.removeProperty("cursor");
    document.body.style.removeProperty("user-select");
  };

  const dispatch = useDispatch();

  const updateLayoutL = useCallback(() => {
    const node = leftPanelRef.current;
    const panel = { pinned: leftPinnedRef.current, open: leftOpenRef.current };
    const width = widthRefL.current;
    let finalWidth = !panel.open ? 1 : width ?? 0;

    if (panel.pinned && width > minSliderWidthLeft) {
      dispatch(SetLeftPanelPinned(false));
    }

    if (panel.open && finalWidth < minSliderWidthLeft) {
      dispatch(SetLeftPanelPinned(true));
      finalWidth = pinnedSliderWidth;
    }

    if (finalWidth > maxSliderWidth) finalWidth = maxSliderWidth;

    if (panel.open && !panel.pinned && finalWidth > minSliderWidthLeft && leftPanelRef.current) {
      keepWidthL.current = finalWidth;
    }

    const gapBetweenPanels = (panel.open ? 8 : 0) + (rightOpenRef.current ? 8 : 0);
    const otherElem = rightPanelRef.current;
    const otherWidth = otherElem && otherElem.getBoundingClientRect().width;
    const centerPanel = centerPanelRef.current;

    if (centerPanel) {
      centerPanel.style.width = `calc(100% - ${finalWidth + (otherWidth ?? 0) + gapBetweenPanels}px)`;
    }
    if (node) {
      node.style.width = `${finalWidth}px`;
    }
  }, []);

  const updateLayoutR = useCallback(() => {
    const node = rightPanelRef.current;
    const panel = { pinned: rightPinnedRef.current, open: rightOpenRef.current };
    const width = widthRefR.current;
    let finalWidth = !panel.open ? 1 : width ?? 0;

    if (panel.pinned && width > minSliderWidthRight) {
      dispatch(SetRightPanelPinned(false));
    }
    if (panel.open && finalWidth < minSliderWidthRight) {
      dispatch(SetRightPanelPinned(true));
      finalWidth = pinnedSliderWidth;
    }
    if (finalWidth > maxSliderWidth) finalWidth = maxSliderWidth;
    if (panel.open && !panel.pinned && finalWidth > minSliderWidthRight && rightPanelRef.current) {
      keepWidthR.current = finalWidth;
    }

    const gapBetweenPanels = (panel.open ? 8 : 0) + (leftOpenRef.current ? 8 : 0);
    const otherElem = leftPanelRef.current;
    const otherWidth = otherElem && otherElem.getBoundingClientRect().width;
    const centerPanel = centerPanelRef.current;

    if (centerPanel) {
      centerPanel.style.width = `calc(100% - ${finalWidth + (otherWidth ?? 0) + gapBetweenPanels}px)`;
    }
    if (node) {
      node.style.width = `${finalWidth}px`;
    }
  }, []);

  useEffect(() => {
    if (leftPanel.open) {
      if (!leftPanel.pinned) {
        widthRefL.current = keepWidthL.current ?? initialWidthL ?? 200;
      } else {
        widthRefL.current = pinnedSliderWidth;
      }
    } else {
      widthRefL.current = 0;
    }
    updateLayoutL();
    if (rightPanel.open) {
      if (!rightPanel.pinned) {
        widthRefR.current = keepWidthR.current ?? initialWidthR ?? 200;
      } else {
        widthRefR.current = pinnedSliderWidth;
      }
    } else {
      widthRefR.current = 0;
    }
    updateLayoutR();
  }, [leftPanel.pinned, rightPanel.pinned, leftPanel.open, rightPanel.open]);
};
